import type { PageStat } from '@/lib/analytics-api';

interface Props {
  pages: PageStat[];
}

function fmt(n: number): string {
  return new Intl.NumberFormat('de-DE').format(n);
}

function fmtDuration(ms: number | null | undefined): string {
  if (!ms) return '–';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} min`;
}

export default function TopPagesTable({ pages }: Props) {
  const top = pages.slice(0, 15);
  // Engagement-Spalten nur zeigen, wenn es überhaupt Event-Daten gibt (t.js v2 live)
  const hasEngagement = top.some((p) => (p.avg_duration_ms || 0) > 0 || (p.contact_clicks || 0) > 0);
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900">Meistbesuchte Seiten</h2>
        {hasEngagement && (
          <p className="text-[11px] text-gray-400 mt-0.5">Lesezeit = aktive Zeit im Tab · Kontakt = Klicks auf Telefon/E-Mail/Buttons</p>
        )}
      </div>
      {top.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-gray-400">Keine Daten im Zeitraum.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-2 font-medium">Seite</th>
                <th className="px-4 py-2 font-medium text-right">Aufrufe</th>
                <th className="px-4 py-2 font-medium text-right">Besucher</th>
                {hasEngagement && <th className="px-4 py-2 font-medium text-right">Lesezeit</th>}
                {hasEngagement && <th className="px-4 py-2 font-medium text-right">Scroll</th>}
                {hasEngagement && (
                  <th className="px-4 py-2 font-medium text-right" title="Klicks auf Telefonnummer oder E-Mail auf dieser Seite — keine Formular-Leads">
                    Kontakt-Klicks
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {top.map((p, i) => (
                <tr key={`${p.site_slug}-${p.path}-${i}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-700 max-w-xs truncate" title={p.path}>
                    {p.path}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmt(p.pageviews)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">{fmt(p.visitors)}</td>
                  {hasEngagement && (
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">{fmtDuration(p.avg_duration_ms)}</td>
                  )}
                  {hasEngagement && (
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                      {p.avg_scroll_pct ? `${p.avg_scroll_pct} %` : '–'}
                    </td>
                  )}
                  {hasEngagement && (
                    <td className={`px-4 py-2 text-right tabular-nums ${p.contact_clicks ? 'text-green-700 font-medium' : 'text-gray-400'}`}>
                      {p.contact_clicks ?? '–'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
